import React, { useState, useEffect, useCallback } from 'react';
import { Filter, Plus, ChevronLeft, ChevronRight, ChevronDown, Download, Upload, X, Trash2, Loader2, CalendarDays, Printer, CalendarSync as CalendarSyncIcon, ScanLine, FileText, Search } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { apiWorkorders, apiCustomers, team, pesticideProducts } from '../../lib/api';
import { toCSV, downloadFile, parseCSV, readFileAsText, pickCell, indexCustomers,
  matchCustomer, isSummaryRow, importKey, normStatus, splitJobLabel,
  isBillingStatus } from '../../lib/importExport';
import { toast } from 'sonner';
import { useJobStatuses, statusBadge } from '../../lib/jobStatus';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import BulkBar, { TickBox } from '../ui/bulk-bar';
import useSelection from '../../lib/useSelection';
import { billJobs, setJobStatus } from '../../lib/jobBulk';
import CustomerCombobox from '../forms/CustomerCombobox';
import CalendarSync from './CalendarSync';
import ReportScanner from './ReportScanner';
import { today, normDate, parseDay, shiftDays, weekOf, prettyDate, shortDate,
  shiftMonths, monthGrid, monthLabel } from '../../lib/dates';
import { printServiceReport } from '../../lib/serviceReport';
import { useConfig } from '../../context/ConfigContext';

// Status list comes from the backend so it cannot drift from the scheduler.
// Cards are tinted from the shared status vocabulary, so a status added on the
// backend styles itself instead of falling through to grey.
const statusColor = (s) => statusBadge(s);
// Kept in step with recurrence.py — the backend refuses a frequency it does not
// recognise rather than guessing, so these strings have to match it exactly.
const FREQUENCIES = ['One-off', 'Weekly', 'Bi-weekly', 'Monthly', 'Every other month',
  'Quarterly', 'Semi-annual', 'Annual'];

const TIMES = ['7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'];

const parseHour = (t) => {
  if (!t) return null;
  const m = String(t).match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i);
  if (!m) return null;
  let h = parseInt(m[1]); const ap = (m[3] || '').toUpperCase();
  if (ap === 'PM' && h < 12) h += 12; if (ap === 'AM' && h === 12) h = 0;
  return h;
};
const hourToTime = (h) => { const ap = h >= 12 ? 'PM' : 'AM'; let hh = h % 12; if (hh === 0) hh = 12; return `${hh}:00 ${ap}`; };

function WorkOrderModal({ initial, customers, techs, onClose, onSaved }) {
  const STATUSES = useJobStatuses();
  const { contact } = useConfig();
  const editing = !!initial?.id;
  const [f, setF] = useState({
    customerId: initial?.customerId || '', customer: initial?.customer || '',
    address: initial?.address || '', service: initial?.service || '',
    date: normDate(initial?.date) || today(), time: initial?.time || '8:00 AM',
    techEmail: initial?.techEmail || '', tech: initial?.tech || '',
    status: initial?.status || 'Scheduled', notes: initial?.notes || '',
    frequency: initial?.frequency || 'One-off', repeatUntil: initial?.repeatUntil || '',
  });
  const [saving, setSaving] = useState(false);

  const pickCustomer = (id) => {
    const c = customers.find(x => x.id === id);
    setF(p => ({ ...p, customerId: id, customer: c?.name || '', address: c ? [c.address, c.city, c.state, c.zip].filter(Boolean).join(', ') : p.address }));
  };
  const pickTech = (email) => { const t = techs.find(x => x.email === email); setF(p => ({ ...p, techEmail: email, tech: t?.name || '' })); };

  const save = async () => {
    if (!f.customer && !f.customerId) { toast.error('Choose a customer'); return; }
    if (!f.service.trim()) { toast.error('Enter a service'); return; }
    setSaving(true);
    try {
      const body = { ...f, date: normDate(f.date) };
      if (editing) { await apiWorkorders.update(initial.id, body); toast.success('Work order updated'); }
      else { await apiWorkorders.create({ ...body, workOrderNumber: 'WO-' + Date.now().toString().slice(-6) }); toast.success('Work order created'); }
      onSaved(); onClose();
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };
  const del = async () => {
    if (!editing) return;
    setSaving(true);
    try { await apiWorkorders.remove(initial.id); toast.success('Work order deleted'); onSaved(); onClose(); }
    catch { toast.error('Delete failed'); } finally { setSaving(false); }
  };

  // Print the real service report for this job, pre-filled and carrying the
  // work order id so a scan of the returned sheet identifies it exactly.
  const printReport = async () => {
    let catalog = [];
    try { catalog = await pesticideProducts.list(); } catch { /* falls back to the built-in list */ }
    const ok = printServiceReport({
      job: { ...initial, ...f, id: initial?.id },
      company: contact,
      products: catalog,
    });
    if (!ok) toast.error('Allow pop-ups to print the service report');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="workorder-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-bold text-slate-900">{editing ? 'Edit Work Order' : 'New Work Order'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label className="text-xs">Customer</Label>
            <CustomerCombobox
              customers={customers}
              value={f.customerId}
              onSelect={(c) => pickCustomer(c.id)}
              placeholder="Search customers…"
              testId="wo-customer-select"
            />
          </div>
          <div><Label className="text-xs">Service Address</Label><Input value={f.address} onChange={e => setF({ ...f, address: e.target.value })} className="mt-1 h-10" data-testid="wo-address-input" /></div>
          {/* How often this service repeats. Future visits are booked onto the
              schedule automatically; none of them is invoiced until the job is
              marked complete. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Repeats</Label>
              <Select value={f.frequency} onValueChange={v => setF({ ...f, frequency: v })}>
                <SelectTrigger className="mt-1 h-10" data-testid="wo-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQUENCIES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Repeat until <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input type="date" value={f.repeatUntil} disabled={f.frequency === 'One-off'}
                onChange={e => setF({ ...f, repeatUntil: e.target.value })}
                className="mt-1 h-10 disabled:bg-slate-50" data-testid="wo-repeat-until" />
            </div>
          </div>
          {f.frequency !== 'One-off' && (
            <p className="text-[11px] text-slate-500 -mt-1">
              Future visits are added to the schedule automatically. Nothing is invoiced until a
              visit is marked complete, so one that never happens can be moved or cancelled freely.
            </p>
          )}
          <div><Label className="text-xs">Service</Label><Input value={f.service} onChange={e => setF({ ...f, service: e.target.value })} placeholder="e.g. Quarterly Pest Treatment" className="mt-1 h-10" data-testid="wo-service-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date</Label><Input type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} className="mt-1 h-10" data-testid="wo-date-input" /></div>
            <div><Label className="text-xs">Time</Label><Select value={f.time} onValueChange={v => setF({ ...f, time: v })}><SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger><SelectContent>{TIMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Technician</Label>
              <Select value={f.techEmail} onValueChange={pickTech}>
                <SelectTrigger className="mt-1 h-10" data-testid="wo-tech-select"><SelectValue placeholder="Assign…" /></SelectTrigger>
                <SelectContent>{techs.map(t => <SelectItem key={t.email} value={t.email}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Status</Label><Select value={f.status} onValueChange={v => setF({ ...f, status: v })}><SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.value}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label className="text-xs">Notes</Label><textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={2} className="mt-1 w-full text-sm border border-slate-200 rounded-md p-2" /></div>
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={saving} className="flex-1 bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="wo-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Save Changes' : 'Create Work Order')}</Button>
            {editing && <Button onClick={printReport} variant="outline" className="gap-1.5" data-testid="wo-print-report-btn"><Printer className="w-4 h-4" />Service Report</Button>}
            {editing && <Button onClick={del} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="wo-delete-btn"><Trash2 className="w-4 h-4" /></Button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// How many rows the list view paints at most. The book runs to thousands of
// visits and a table that long locks the browser up on her phone.
const LIST_CAP = 500;

export default function SchedulePage() {
  const STATUSES = useJobStatuses();
  const [date, setDate] = useState(today());
  const [view, setView] = useState('day');
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {} | job
  const [syncOpen, setSyncOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [dragId, setDragId] = useState(null);
  // The list view's own scope and search: "this day" keeps the old behaviour,
  // "all jobs" is what billing a batch needs.
  const [listScope, setListScope] = useState('day');
  const [listQ, setListQ] = useState('');
  const [moving, setMoving] = useState(null);   // job armed for tap-to-move

  const load = useCallback(async () => {
    try {
      const [wo, cust, tk] = await Promise.all([
        apiWorkorders.list().catch(() => []),
        apiCustomers.list().catch(() => []),
        team.technicians().catch(() => []),
      ]);
      setJobs(wo || []); setCustomers(cust || []); setTechs(tk || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const shiftDay = (n) => setDate(shiftDays(date, n));
  const dayJobs = jobs.filter(j => normDate(j.date) === date);
  const hours = Array.from({ length: 11 }, (_, i) => 7 + i);

  // Week view: 7 days starting Sunday of the selected week
  const weekDays = weekOf(date);
  const shiftMonth = (n) => setDate(shiftMonths(date, n));
  // A fortnight is the window the office plans in: long enough to see next week
  // filling up, short enough to read in one go.
  const AGENDA_DAYS = 14;
  const agendaEnd = shiftDays(date, AGENDA_DAYS - 1);
  const shiftAgenda = (n) => setDate(shiftDays(date, n * AGENDA_DAYS));
  // Normalised dates sort correctly as plain strings, which is the whole point of
  // writing them YYYY-MM-DD, so no date arithmetic is needed to order these.
  const agendaGroups = (() => {
    const byDay = new Map();
    for (const j of jobs) {
      const d = normDate(j.date);
      if (!d || d < date || d > agendaEnd) continue;
      if (!byDay.has(d)) byDay.set(d, []);
      byDay.get(d).push(j);
    }
    return [...byDay.keys()].sort().map(d => ({
      date: d,
      jobs: byDay.get(d).sort((a, b) => (parseHour(a.time) || 0) - (parseHour(b.time) || 0)),
    }));
  })();
  const monthDays = monthGrid(date);
  const shiftWeek = (n) => setDate(shiftDays(date, n * 7));

  // The list is where the office works through jobs in bulk, so it can show the
  // whole book rather than only the day on screen: a year of imported visits is
  // never all on one day, and billing them a row at a time is the thing that
  // made this screen unusable. Tick boxes act on the rows listed and nothing
  // else, so narrowing the search narrows what a bulk action touches.
  const listRows = (() => {
    const base = listScope === 'all' ? jobs : dayJobs;
    const needle = listQ.trim().toLowerCase();
    const rows = needle
      ? base.filter(j => [j.customer, j.address, j.service, j.tech,
                          j.workOrderNumber, j.status]
          .filter(Boolean).join(' ').toLowerCase().includes(needle))
      : base.slice();
    return listScope === 'all'
      ? rows.sort((a, b) => normDate(b.date).localeCompare(normDate(a.date))).slice(0, LIST_CAP)
      : rows.sort((a, b) => (parseHour(a.time) || 0) - (parseHour(b.time) || 0));
  })();
  const jobSel = useSelection(listRows.map(j => j.id));
  const bulkInvoice = async () => {
    if (await billJobs(jobSel.chosen)) { jobSel.clear(); load(); }
  };
  const bulkStatus = async (status) => {
    if (await setJobStatus(jobSel.chosen, status)) { jobSel.clear(); load(); }
  };

  // Persist a drag-and-drop reschedule (optimistic, then PUT)
  const reschedule = async (jobId, patch) => {
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;
    const same = Object.entries(patch).every(([k, v]) => job[k] === v);
    if (same) return;
    const updated = { ...job, ...patch };
    setJobs(js => js.map(j => j.id === jobId ? updated : j));
    try { await apiWorkorders.update(jobId, updated); toast.success('Job rescheduled'); }
    catch { toast.error('Could not reschedule'); load(); }
  };
  // HTML5 drag events never fire on a touch screen, so on a phone or the
  // installed Android app the calendar was completely inert — you could not
  // move an appointment at all. `moving` drives a tap-to-move path that works
  // everywhere: tap Move on a job, then tap the slot or day to send it there.
  // Desktop drag-and-drop is untouched and still works with a mouse.
  const dropOnSlot = (techEmail, techName, hour) => {
    const id = dragId || moving?.id;
    if (!id) return;
    reschedule(id, { techEmail, tech: techName, time: hourToTime(hour) });
    setDragId(null);
    setMoving(null);
  };
  const dropOnDay = (newDate) => {
    const id = dragId || moving?.id;
    if (!id) return;
    reschedule(id, { date: newDate });
    setDragId(null);
    setMoving(null);
  };

  // A row whose date could not be read used to take whatever day was showing on
  // screen, silently, and it did that for every row: a file whose date column was
  // spelled any other way put the whole import on one day. A job's date decides
  // where a technician drives, so a row without one is left out and said so.
  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const parsed = parseCSV(await readFileAsText(file));
    // A work order with no customerId is invisible to everything that reasons
    // about a customer over time: the service tracker's last visit, when the next
    // recurring visit is due, and therefore whether anyone is overdue. The name in
    // a CSV is the only handle on the record, so match on it here rather than
    // import history that no screen can connect to a customer.
    const index = indexCustomers(customers);

    const rows = parsed
      .filter(r => !isSummaryRow(r, ['ID', 'Work Order', 'WO', 'Invoice #', 'Document #']))
      .map((r, i) => {
      const name = pickCell(r, 'Customer', 'Customer Name');
      const account = pickCell(r, 'Account #', 'Account Number', 'Accountnumber', 'Account');
      const rawDate = pickCell(r, 'Date', 'Service Date', 'Scheduled Date', 'Start Date', 'Job Created Date');
      // The invoice export keeps the service and the address together in one Job
      // cell and has no column for either, so the only way to read a visit's
      // address out of that file is to take it from there.
      const invoice = pickCell(r, 'Invoice #', 'Invoice Number');
      const fromJob = splitJobLabel(pickCell(r, 'Job'));
      const address = pickCell(r, 'Address', 'Service Address') || fromJob.address;
      // Several customers hold more than one account under one name (three Salud
      // Juice sites, four Nikar properties), so the address is what decides whose
      // service history the visit joins. Without it the row stays unmatched
      // rather than being filed against the wrong property.
      const cust = matchCustomer(index, { account, name, address });
      const service = pickCell(r, 'Service', 'Service Type') || fromJob.service;
      const given = pickCell(r, 'ID', 'Work Order', 'WO');
      const rawStatus = pickCell(r, 'Status', 'Job Status');
      const date = normDate(rawDate);
      return {
        line: i + 2,   // the header is line 1
        date,
        matched: !!cust,
        // A void invoice is a bill that was cancelled, so there is no visit to
        // record. Importing it would add a service to the customer's history and
        // push their next visit out by a whole interval.
        voided: !!invoice && /^void(ed)?$/i.test(rawStatus.trim()),
        job: {
          // A file with no ID of its own gets a key derived from the row, so a
          // second import of the same export adds nothing. The raw date is used
          // rather than the normalised one because it carries the time of day,
          // which is what separates two visits to the same address on one day.
          workOrderNumber: given || (invoice ? `GD-INV-${invoice}`
            : `WO-imp-${importKey([name, account, address, service, rawDate])}`),
          time: pickCell(r, 'Time', 'Start Time') || '8:00 AM',
          customerId: cust?.id || '',
          customer: cust?.name || name,
          address: address
            || (cust ? [cust.address, cust.city, cust.state, cust.zip].filter(Boolean).join(', ') : ''),
          service,
          tech: pickCell(r, 'Tech', 'Technician'),
          // "Paid" and "Draft" describe the bill, not the visit. A billed visit
          // in the past happened; one dated ahead has not yet.
          status: isBillingStatus(rawStatus)
            ? (date && date <= today() ? 'Completed' : 'Scheduled')
            : normStatus(rawStatus) || 'Scheduled',
        },
      };
    });
    const voided = rows.filter(r => r.voided).length;
    const live = rows.filter(r => !r.voided);
    const dated = live.filter(r => r.date);
    const undated = live.filter(r => !r.date);
    if (!dated.length) {
      const cols = Object.keys(parsed[0] || {}).join(', ');
      toast.error(`No date column was recognised, so nothing was imported.${cols ? ` The file's columns are: ${cols}` : ''}`);
      e.target.value = '';
      return;
    }
    // Two rows of one file can be identical in every column — helen's export has
    // four jobs for one restaurant stamped the same minute, because a recurring
    // series is created in one go. Counting occurrences rather than collapsing
    // them keeps all four, and because the count depends only on the file's
    // contents, re-importing the same file produces the same keys and so adds
    // nothing. This is how the payments importer handles the same problem.
    const occurrences = new Map();
    const mapped = dated.map(r => {
      const base = r.job.workOrderNumber;
      const n = (occurrences.get(base) || 0) + 1;
      occurrences.set(base, n);
      const number = n === 1 ? base : `${base}-${n}`;
      return { ...r.job, workOrderNumber: number, date: r.date, id: `WO-${number}` };
    });
    try {
      const res = await apiWorkorders.bulk(mapped);
      await load();
      // The server reports what it wrote, not what it was sent: re-importing the
      // same export is meant to add nothing, and the office needs to see that
      // rather than a success message for rows that were already there.
      const added = res?.inserted ?? mapped.length;
      const already = res?.skipped ?? 0;
      toast.success(`Imported ${added} work order${added === 1 ? '' : 's'}`
        + (already ? `, and skipped ${already} already on file` : ''));
      const unmatched = dated.filter(r => !r.matched).length;
      if (unmatched) {
        toast.warning(`${unmatched} of them could not be matched to a customer on file, so they will not count towards that customer's service history. Check the names against your customer list.`);
      }
      if (voided) {
        toast.warning(`${voided} void invoice${voided === 1 ? '' : 's'} ${voided === 1 ? 'was' : 'were'} left out, because a cancelled bill is not a visit.`);
      }
      if (undated.length) {
        const shown = undated.slice(0, 5).map(r => r.line).join(', ');
        toast.warning(`${undated.length} row${undated.length === 1 ? '' : 's'} had no readable date and ${undated.length === 1 ? 'was' : 'were'} left out (line${undated.length === 1 ? '' : 's'} ${shown}${undated.length > 5 ? '\u2026' : ''})`);
      }
    } catch { toast.error('Import failed'); }
    e.target.value = '';
  };
  const onExport = () => {
    const csv = toCSV(jobs, [{ key: 'workOrderNumber', header: 'ID' }, { key: 'date', header: 'Date' }, { key: 'time', header: 'Time' }, { key: 'customer', header: 'Customer' }, { key: 'address', header: 'Address' }, { key: 'service', header: 'Service' }, { key: 'tech', header: 'Tech' }, { key: 'status', header: 'Status' }]);
    downloadFile('xpertpests-workorders.csv', csv); toast.success('Exported workorders.csv');
  };
  const quickStatus = async (job, status) => {
    setJobs(js => js.map(j => j.id === job.id ? { ...j, status } : j));
    try { await apiWorkorders.update(job.id, { ...job, status }); } catch { toast.error('Could not update'); load(); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div className="flex flex-wrap justify-between gap-3 items-center">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Schedule</h1>
          <p className="text-slate-500 mt-1">{jobs.length} work orders · {dayJobs.length} on this day · {techs.length} technician(s)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 px-4 h-10 bg-white border border-slate-200 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer"><Upload className="w-4 h-4" />Import<input type="file" accept=".csv" className="hidden" onChange={onImport} /></label>
          <Button variant="outline" onClick={onExport} className="gap-1.5"><Download className="w-4 h-4" />Export</Button>
          <Button variant="outline" onClick={() => setSyncOpen(true)} className="gap-1.5" data-testid="calendar-sync-btn"><CalendarSyncIcon className="w-4 h-4" />Calendar sync</Button>
          <Button variant="outline" onClick={() => setScanOpen(true)} className="gap-1.5" data-testid="scan-report-btn"><ScanLine className="w-4 h-4" />Scan report</Button>
          <Button className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" onClick={() => setModal({})} data-testid="new-workorder-btn"><Plus className="w-4 h-4" />New Work Order</Button>
        </div>
      </div>

      <Card className="p-4 border-slate-200">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          {/* flex-wrap, and no minimum width until there is room for one: at
              390px this row lays out 430px wide, which pushed Today off the
              screen and made the whole page zoom out to fit it. */}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="icon" variant="outline" className="h-9 w-9 flex-shrink-0" onClick={() => view === 'agenda' ? shiftAgenda(-1) : view === 'month' ? shiftMonth(-1) : view === 'week' ? shiftWeek(-1) : shiftDay(-1)} data-testid="prev-day-btn"><ChevronLeft className="w-4 h-4" /></Button>
            <div className="font-bold text-slate-900 text-lg min-w-0 sm:min-w-[260px] text-center" data-testid="schedule-date">
              {/* parseDay, not new Date: `new Date('2026-10-04')` is midnight UTC,
                  which is the evening of the 3rd here, so this range used to name
                  the wrong days either side of the week it was showing. */}
              {view === 'agenda'
                ? `${parseDay(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${parseDay(agendaEnd).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
                : view === 'month'
                ? monthLabel(date)
                : view === 'week'
                ? `${parseDay(weekDays[0]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${parseDay(weekDays[6]).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
                : (<><span className="sm:hidden">{shortDate(date)}</span><span className="hidden sm:inline">{prettyDate(date)}</span></>)}
            </div>
            <Button size="icon" variant="outline" className="h-9 w-9 flex-shrink-0" onClick={() => view === 'agenda' ? shiftAgenda(1) : view === 'month' ? shiftMonth(1) : view === 'week' ? shiftWeek(1) : shiftDay(1)} data-testid="next-day-btn"><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" className="sm:ml-2 flex-shrink-0" onClick={() => setDate(today())} data-testid="today-btn">Today</Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden md:inline text-xs text-slate-400 mr-1">Drag a job, or tap Move, to reschedule</span>
            {['day', 'week', 'month', 'agenda', 'list'].map(v => (
              // Measured at 32px tall on a phone. Each one does respond to a
              // scripted tap, so what the office was hitting was the gap between
              // them rather than a dead button - reported as "the time tabs are
              // not clickable". 44px is the smallest target a thumb finds.
              <button key={v} onClick={() => setView(v)} className={`px-4 min-h-[44px] sm:min-h-0 sm:px-3 sm:py-1.5 rounded-md text-sm font-semibold capitalize transition ${view === v ? 'bg-[#8BC53F] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-testid={`view-${v}`}>{v}</button>
            ))}
          </div>
        </div>
      </Card>

      {moving && (
        <div className="sticky top-2 z-20 flex items-center gap-3 rounded-lg border border-[#8BC53F] bg-[#8BC53F]/10 px-4 py-2.5 text-sm" data-testid="moving-banner">
          <span className="font-semibold text-slate-900 truncate">
            Moving: {moving.customer || 'job'}{moving.time ? ` · ${moving.time}` : ''}
          </span>
          <span className="text-slate-600 truncate">
            {view === 'day' ? 'Tap a time slot to move it there.' : 'Tap a day to move it there.'}
          </span>
          <button
            onClick={() => setMoving(null)}
            className="ml-auto shrink-0 px-2 py-1 rounded border border-slate-300 bg-white text-xs font-semibold text-slate-700"
            data-testid="moving-cancel"
          >Cancel</button>
        </div>
      )}

      {loading ? <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        // Only the day and week grids need a column per technician. The month
        // overview and the list are about the jobs themselves, and hiding them
        // behind this check made a schedule with jobs on it look empty.
        : techs.length === 0 && (view === 'day' || view === 'week') ? (
        <Card className="p-10 border-slate-200 text-center text-slate-500">No technician accounts yet. Create tech users to dispatch jobs, or use the month and list views.</Card>
      ) : view === 'day' ? (
        <Card className="border-slate-200 overflow-x-auto">
          <div className="min-w-[640px]">
            <div className="grid border-b border-slate-200 bg-slate-50" style={{ gridTemplateColumns: `60px repeat(${techs.length}, minmax(140px, 1fr))` }}>
              <div className="p-3 text-xs font-bold text-slate-500">TIME</div>
              {techs.map((t, i) => (
                <div key={t.email} className="p-3 border-l border-slate-200 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full text-white text-xs font-bold flex items-center justify-center" style={{ background: ['#8BC53F', '#6FA82C', '#a855f7', '#f97316', '#0ea5e9'][i % 5] }}>{(t.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('')}</div>
                  <div><div className="text-sm font-bold text-slate-900">{t.name}</div><div className="text-[11px] text-slate-500">{dayJobs.filter(j => j.techEmail === t.email || j.tech === t.name).length} jobs</div></div>
                </div>
              ))}
            </div>
            {hours.map(h => (
              <div key={h} className="grid border-b border-slate-100 min-h-[64px]" style={{ gridTemplateColumns: `60px repeat(${techs.length}, minmax(140px, 1fr))` }}>
                <div className="p-2 text-xs font-semibold text-slate-500">{h > 12 ? h - 12 : h}:00 {h >= 12 ? 'PM' : 'AM'}</div>
                {techs.map((t) => {
                  const job = dayJobs.find(j => parseHour(j.time) === h && (j.techEmail === t.email || j.tech === t.name));
                  return (
                    <div
                      key={t.email + h}
                      className={`border-l border-slate-200 p-1.5 transition-colors data-[over=true]:bg-[#8BC53F]/10 ${job ? '' : 'cursor-pointer hover:bg-slate-50'}`}
                      title={job ? undefined : `Book ${hourToTime(h)} with ${t.name}`}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.dataset.over = 'true'; }}
                      onDragLeave={(e) => { e.currentTarget.dataset.over = 'false'; }}
                      onDrop={(e) => { e.currentTarget.dataset.over = 'false'; dropOnSlot(t.email, t.name, h); }}
                      // An empty slot books that slot. The office's instinct, coming
                      // from every other scheduler, is to click the gap in the day
                      // rather than hunt for a New Work Order button, and the slot
                      // already knows the technician and the hour.
                      onClick={() => {
                        if (moving) { dropOnSlot(t.email, t.name, h); return; }
                        if (!job) setModal({ date, time: hourToTime(h), techEmail: t.email, tech: t.name });
                      }}
                      data-over={moving && !job ? 'true' : undefined}
                      data-testid={`slot-${t.email}-${h}`}
                    >
                      {job && (
                        <div className="relative">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setMoving(moving?.id === job.id ? null : job); }}
                          className={`absolute top-1 right-1 z-10 px-1.5 py-0.5 rounded text-[9px] font-bold border ${moving?.id === job.id ? 'bg-[#8BC53F] text-white border-[#8BC53F]' : 'bg-white/90 text-slate-600 border-slate-300'}`}
                          data-testid={`job-move-${job.id}`}
                        >{moving?.id === job.id ? 'Moving' : 'Move'}</button>
                        <button
                          draggable
                          onDragStart={() => setDragId(job.id)}
                          onDragEnd={() => setDragId(null)}
                          onClick={() => setModal(job)}
                          className={`w-full text-left rounded-md p-2 border ${statusColor(job.status)} cursor-grab active:cursor-grabbing hover:shadow-md transition ${dragId === job.id ? 'opacity-40' : ''}`}
                          data-testid={`job-${job.id}`}
                        >
                          <div className="text-xs font-bold truncate text-slate-900">{job.customer}</div>
                          <div className="text-[10px] text-slate-600 truncate">{job.service}</div>
                          <Badge className={`${statusColor(job.status)} text-[9px] font-medium border-0 mt-1`}>{job.status}</Badge>
                        </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Card>
      ) : view === 'week' ? (
        <Card className="border-slate-200 overflow-x-auto">
          <div className="grid min-w-[900px]" style={{ gridTemplateColumns: 'repeat(7, minmax(120px, 1fr))' }}>
            {weekDays.map((d) => {
              const isToday = d === today();
              const dj = jobs.filter(j => normDate(j.date) === d).sort((a, b) => (parseHour(a.time) || 0) - (parseHour(b.time) || 0));
              return (
                <div
                  key={d}
                  className="border-l border-slate-200 first:border-l-0 min-h-[420px] transition-colors data-[over=true]:bg-[#8BC53F]/10"
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.dataset.over = 'true'; }}
                  onDragLeave={(e) => { e.currentTarget.dataset.over = 'false'; }}
                  onDrop={(e) => { e.currentTarget.dataset.over = 'false'; dropOnDay(d); }}
                  onClick={() => { if (moving) dropOnDay(d); }}
                  data-over={moving ? 'true' : undefined}
                  data-testid={`week-day-${d}`}
                >
                  <button
                    onClick={() => { setDate(d); setView('day'); }}
                    className={`w-full text-center py-2 border-b sticky top-0 ${isToday ? 'bg-[#8BC53F] text-white border-[#8BC53F]' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wide">{parseDay(d).toLocaleDateString(undefined, { weekday: 'short' })}</div>
                    <div className="text-lg font-extrabold leading-none">{parseDay(d).getDate()}</div>
                    <div className={`text-[10px] ${isToday ? 'text-white/80' : 'text-slate-400'}`}>{dj.length} job{dj.length === 1 ? '' : 's'}</div>
                  </button>
                  <div className="p-1.5 space-y-1.5">
                    {dj.map(job => (
                      <div key={job.id} className="relative">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setMoving(moving?.id === job.id ? null : job); }}
                          className={`absolute top-1 right-1 z-10 px-1.5 py-0.5 rounded text-[9px] font-bold border ${moving?.id === job.id ? 'bg-[#8BC53F] text-white border-[#8BC53F]' : 'bg-white/90 text-slate-600 border-slate-300'}`}
                          data-testid={`week-job-move-${job.id}`}
                        >{moving?.id === job.id ? 'Moving' : 'Move'}</button>
                        <button
                          draggable
                          onDragStart={() => setDragId(job.id)}
                          onDragEnd={() => setDragId(null)}
                          onClick={() => setModal(job)}
                          className={`w-full text-left rounded-md p-1.5 border ${statusColor(job.status)} cursor-grab active:cursor-grabbing hover:shadow-md transition ${dragId === job.id ? 'opacity-40' : ''}`}
                          data-testid={`week-job-${job.id}`}
                        >
                          <div className="text-[10px] font-bold text-slate-500">{job.time}</div>
                          <div className="text-xs font-bold truncate text-slate-900">{job.customer}</div>
                          <div className="text-[10px] text-slate-600 truncate">{job.service}{job.tech ? ` · ${job.tech}` : ''}</div>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ) : view === 'agenda' ? (
        // Read straight down a fortnight. Days with nothing on them are left out
        // on purpose: an agenda is for what is actually booked, and the month grid
        // is already there for seeing the gaps.
        <Card className="border-slate-200 overflow-hidden" data-testid="agenda-view">
          {agendaGroups.length === 0 ? (
            <div className="px-4 py-12 text-center text-slate-400">
              Nothing booked between {shortDate(date)} and {shortDate(agendaEnd)}.
            </div>
          ) : agendaGroups.map(({ date: d, jobs: dj }) => (
            <div key={d} data-testid={`agenda-day-${d}`}>
              <button
                onClick={() => { setDate(d); setView('day'); }}
                className={`w-full flex items-center gap-3 px-4 py-2 border-y border-slate-200 text-left sticky top-0 ${d === today() ? 'bg-[#8BC53F] text-white' : 'bg-slate-50 hover:bg-slate-100'}`}
                title={`Open ${prettyDate(d)}`}
              >
                <span className="font-bold text-sm">{shortDate(d)}</span>
                {d === today() && <span className="text-[10px] font-bold uppercase tracking-wide">Today</span>}
                <span className={`ml-auto text-xs ${d === today() ? 'text-white/80' : 'text-slate-500'}`}>
                  {dj.length} job{dj.length === 1 ? '' : 's'}
                </span>
              </button>
              <div className="divide-y divide-slate-100">
                {dj.map(job => (
                  <button
                    key={job.id}
                    onClick={() => setModal(job)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start gap-3"
                    data-testid={`agenda-job-${job.id}`}
                  >
                    <span className="text-xs font-bold text-slate-500 w-[72px] flex-shrink-0 pt-0.5">{job.time}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-slate-900 truncate">{job.customer || 'No customer'}</span>
                      <span className="block text-xs text-slate-500 truncate">
                        {job.service}{job.address ? ` \u00b7 ${job.address}` : ''}
                      </span>
                    </span>
                    <span className="hidden sm:block text-xs text-slate-500 w-[110px] flex-shrink-0 truncate pt-0.5">{job.tech}</span>
                    <Badge className={`${statusColor(job.status)} text-[10px] font-medium border-0 flex-shrink-0`}>{job.status}</Badge>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </Card>
      ) : view === 'month' ? (
        // A month at a glance, which is how the office thinks about a recurring
        // round: whole weeks so the columns line up, six rows so the grid does not
        // jump height between months, and a day opens in the day view. Moving a
        // job by dropping it on a day works here exactly as it does in the week.
        <Card className="border-slate-200 overflow-x-auto">
          <div className="min-w-[700px]">
            <div className="grid bg-slate-50 border-b border-slate-200" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(w => (
                <div key={w} className="px-2 py-2 text-[10px] font-bold uppercase tracking-wide text-slate-500 text-center">{w}</div>
              ))}
            </div>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {monthDays.map(({ date: d, inMonth }) => {
                const isToday = d === today();
                const dj = jobs.filter(j => normDate(j.date) === d)
                  .sort((a, b) => (parseHour(a.time) || 0) - (parseHour(b.time) || 0));
                return (
                  <div
                    key={d}
                    className={`border-l border-t border-slate-200 min-h-[104px] p-1 transition-colors data-[over=true]:bg-[#8BC53F]/10 ${inMonth ? '' : 'bg-slate-50/60'}`}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.dataset.over = 'true'; }}
                    onDragLeave={(e) => { e.currentTarget.dataset.over = 'false'; }}
                    onDrop={(e) => { e.currentTarget.dataset.over = 'false'; dropOnDay(d); }}
                    onClick={() => { if (moving) dropOnDay(d); }}
                    data-over={moving ? 'true' : undefined}
                    data-testid={`month-day-${d}`}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); setDate(d); setView('day'); }}
                      className={`w-full flex items-center justify-between px-1 rounded ${isToday ? 'bg-[#8BC53F] text-white' : 'hover:bg-slate-100'}`}
                      title={`Open ${prettyDate(d)}`}
                    >
                      <span className={`text-xs font-bold ${isToday ? '' : inMonth ? 'text-slate-900' : 'text-slate-400'}`}>{parseDay(d).getDate()}</span>
                      {dj.length > 0 && (
                        <span className={`text-[10px] font-semibold ${isToday ? 'text-white/80' : 'text-slate-400'}`}>{dj.length}</span>
                      )}
                    </button>
                    <div className="mt-1 space-y-1">
                      {/* Three, then a count. A busy day has to stay the same height
                          as a quiet one or the grid stops reading as a calendar. */}
                      {dj.slice(0, 3).map(job => (
                        <button
                          key={job.id}
                          draggable
                          onDragStart={() => setDragId(job.id)}
                          onDragEnd={() => setDragId(null)}
                          onClick={(e) => { e.stopPropagation(); setModal(job); }}
                          className={`w-full text-left rounded px-1 py-0.5 border ${statusColor(job.status)} cursor-grab active:cursor-grabbing hover:shadow transition ${dragId === job.id ? 'opacity-40' : ''}`}
                          data-testid={`month-job-${job.id}`}
                        >
                          <div className="text-[10px] font-bold truncate text-slate-900">{job.customer || job.service || 'Job'}</div>
                          <div className="text-[9px] text-slate-600 truncate">{job.time}{job.tech ? ` \u00b7 ${job.tech}` : ''}</div>
                        </button>
                      ))}
                      {dj.length > 3 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDate(d); setView('day'); }}
                          className="w-full text-left text-[10px] font-semibold text-slate-500 hover:text-slate-800 px-1"
                          data-testid={`month-more-${d}`}
                        >+{dj.length - 3} more</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1">
              {[['day', 'This day'], ['all', 'All jobs']].map(([v, label]) => (
                <button key={v} onClick={() => { setListScope(v); jobSel.clear(); }}
                  className={`px-3 py-1.5 rounded text-sm font-semibold transition ${listScope === v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                  data-testid={`list-scope-${v}`}>{label}</button>
              ))}
            </div>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={listQ} onChange={(e) => setListQ(e.target.value)}
                placeholder="Search customer, address, service…" className="pl-9 h-10"
                data-testid="list-search-input" />
            </div>
            <span className="text-xs text-slate-500" data-testid="list-count">
              {listRows.length} shown{listScope === 'all' && listRows.length >= LIST_CAP ? ` (first ${LIST_CAP})` : ''}
            </span>
          </div>

          <BulkBar count={jobSel.count} onClear={jobSel.clear} testId="list-bulk-bar"
                   countTestId="list-bulk-count">
            <Button size="sm" onClick={bulkInvoice} className="h-8 gap-1 bg-[#8BC53F] hover:bg-[#6FA82C] text-white" data-testid="list-bulk-invoice-btn">
              <FileText className="w-3.5 h-3.5" />Create invoices
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="h-8 gap-1 bg-white" data-testid="list-bulk-status-btn">
                  Change status <ChevronDown className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
                {STATUSES.map(st => (
                  <DropdownMenuItem key={st.value} onClick={() => bulkStatus(st.value)}
                                    data-testid={`list-bulk-status-${st.value}`}>
                    Mark as {st.value}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </BulkBar>

          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <TickBox checked={jobSel.allPicked} onChange={jobSel.toggleAll}
                           label="Select every job listed" testId="list-select-all" />
                </th>
                <th className="px-4 py-3 text-left">{listScope === 'all' ? 'Date' : 'Time'}</th><th className="px-4 py-3 text-left">Customer</th><th className="px-4 py-3 text-left">Service</th><th className="px-4 py-3 text-left">Tech</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {listRows.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">{listQ ? 'No work orders match that search.' : listScope === 'all' ? 'No work orders yet. Click “New Work Order”.' : 'No work orders on this day. Click “New Work Order”.'}</td></tr> :
                listRows.map(job => (
                  <tr key={job.id} className={`hover:bg-slate-50 ${jobSel.has(job.id) ? 'bg-[#8BC53F]/5' : ''}`} data-testid={`list-job-${job.id}`}>
                    <td className="px-4 py-3">
                      <TickBox checked={jobSel.has(job.id)} onChange={() => jobSel.toggle(job.id)}
                               label={`Select job ${job.workOrderNumber || job.id}`}
                               testId={`list-select-${job.id}`} />
                    </td>
                    <td className="px-4 py-3 font-medium whitespace-nowrap">{listScope === 'all' ? normDate(job.date) : job.time}</td>
                    <td className="px-4 py-3"><div className="font-semibold text-slate-900">{job.customer}</div><div className="text-xs text-slate-500">{job.address}</div></td>
                    <td className="px-4 py-3 text-slate-600">{job.service}</td>
                    <td className="px-4 py-3 text-slate-600">{job.tech || '—'}</td>
                    <td className="px-4 py-3">
                      <Select value={job.status} onValueChange={v => quickStatus(job, v)}>
                        <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.value}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => setModal(job)} data-testid={`edit-job-${job.id}`}>Edit</Button></td>
                  </tr>
                ))}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {modal && <WorkOrderModal initial={modal} customers={customers} techs={techs} onClose={() => setModal(null)} onSaved={load} />}
      {syncOpen && <CalendarSync onClose={() => setSyncOpen(false)} onImported={load} />}
      {scanOpen && <ReportScanner onClose={() => setScanOpen(false)} onFiled={load} />}
    </div>
  );
}
